import Button from '@/common/components/Button';
import Modal from '@/common/components/Modal';
import SelectController from '@/common/components/SelectController';
import { useCompanyUsers } from '@/common/hooks/queries/useCompanyUsers';
import useDebounce from '@/common/hooks/useDebounce';
import useModalMessagesSimple from '@/common/hooks/useModalMessagesSimple';
import { Mapper } from '@/common/services/mapper';
import { UserData } from '@/common/types/users';
import { FormTransformers } from '@/common/utils/formTransformers';
import { checkoutEntryOptions } from '@/features/maintenanceWorkflow/pages/Show/features/forwardings/constants/directiveEntryFormSteps/checkoutEntry/checkoutEntryOptions.constant';
import { MaintenanceForwardingsCheckinEntryStepOptions } from '@/features/maintenanceWorkflow/pages/Show/features/forwardings/constants/maintenanceForwardingsShowDirectiveDetails.type';
import { useChangeCheckoutEntry } from '@/features/maintenanceWorkflow/pages/Show/hooks/forwardings/checkoutEntry/useChangeCheckoutEntry';
import { useForwardingTabContext } from '@/features/maintenanceWorkflow/pages/Show/hooks/forwardings/useForwardingTabContext';
import { changeCheckoutEntrySchema, ChangeCheckoutEntryValidationSchema } from '@/features/maintenanceWorkflow/pages/Show/schemas/forwardings/checkoutEntry/changeCheckoutEntry.schema';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

const ChangeCheckoutEntry = () => {
  const { forwarding } = useForwardingTabContext()

  const queryClient = useQueryClient()

  const { setMessages } = useModalMessagesSimple({ isError: true })

  const [isOpen, setIsOpen] = useState(false)
  const [searchUser, setSearchUser] = useDebounce('')

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ChangeCheckoutEntryValidationSchema>({
    resolver: zodResolver(changeCheckoutEntrySchema),
    defaultValues: {
      checkout_entry_type: {
        value: MaintenanceForwardingsCheckinEntryStepOptions.AutoEntry,
        label: checkoutEntryOptions[MaintenanceForwardingsCheckinEntryStepOptions.AutoEntry].title,
      },
      responsible: forwarding?.checkout_entry?.responsible ? {
        value: forwarding?.checkout_entry?.responsible?.id,
        label: forwarding?.checkout_entry?.responsible?.full_name,
      } : undefined,
    },
  })

  const {
    data: usersOptions,
    isPending: usersIsPending,
  } = useCompanyUsers({
    filters: { search: searchUser },
    mapper: Mapper.mapToOptions<UserData>({
      valueFieldName: 'id',
      labelFieldName: 'full_name',
    }),
  })

  const {
    mutate: changeCheckoutEntry,
    isPending: changeCheckoutEntryIsPending,
  } = useChangeCheckoutEntry({
    forwardingId: forwarding?.id,
    options: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['maintenanceForwarding'] })
        setIsOpen(false)
        reset()
      },
      onError: FormTransformers.errorsTransformer({ setMessages }),
    },
  })

  useEffect(() => {
    reset({
      checkout_entry_type: {
        value: MaintenanceForwardingsCheckinEntryStepOptions.AutoEntry,
        label: checkoutEntryOptions[MaintenanceForwardingsCheckinEntryStepOptions.AutoEntry].title,
      },
      responsible: forwarding?.checkout_entry?.responsible ? {
        value: forwarding?.checkout_entry?.responsible?.id,
        label: forwarding?.checkout_entry?.responsible?.full_name,
      } : undefined,
    })
  }, [isOpen])

  return (
    <Modal
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      title="Editar método de check-out"
      Trigger={
        <Button
          variant={
            !forwarding?.checkout_entry
              ? 'default'
              : 'link-white'
          }
          operationKlassToCheckPermission="Client::Maintenance::Forwardings::ChangeCheckoutEntry"
        >
          {!forwarding?.checkout_entry
            ? 'Escolha o método de check-out'
            : 'Editar'}
        </Button>
      }
      bodyClassName="text-white-800"
      useCancellableFooter
      onConfirm={handleSubmit((form) => changeCheckoutEntry(form))}
      confirmButtonIsLoading={changeCheckoutEntryIsPending}
      confirmButtonClassName="bg-primary-blue"
      cancelButtonClassName="bg-white-200"
      body={
        <div className="flex flex-col gap-4">
          <SelectController
            control={control}
            name="checkout_entry_type"
            label="Tipo de checkout"
            markAsRequired
            options={Object.entries(checkoutEntryOptions).map(([value, option]) => ({
              value,
              label: option.title,
            }))}
            errorMessage={errors?.checkout_entry_type?.message}
          />

          <SelectController
            control={control}
            name="responsible"
            label="Responsável pelo transporte"
            markAsRequired
            options={usersOptions}
            onInputChange={setSearchUser}
            isLoading={usersIsPending}
            isDisabled={usersIsPending}
            errorMessage={errors?.responsible?.message}
          />
        </div>
      }
    />
  )
}

export default ChangeCheckoutEntry
